import React, { useState, useEffect, useRef } from 'react';
import logo from '../assets/image.png';
import {
  SearchIcon,
  ChevronDown,
  MenuIcon,
  CloseIcon,
  HistoryIcon,
  BishopIcon,
  GovernanceIcon,
  ConstitutionIcon,
  CollegeIcon,
  SchoolIcon,
  HospitalIcon,
  RetreatCenterIcon,
  BookShopIcon,
  ChildCareIcon,
  SocialBoardIcon,
  MagazineIcon,
  LiturgyIcon
} from './Icons';

// ==========================================
// NAVIGATION DATA STRUCTURE
// ==========================================
const NAVIGATION_ITEMS = [
  { name: 'Home', href: '#home', isLink: true },
  {
    name: 'About Diocese',
    href: '#about',
    hasMegaMenu: true,
    columns: 2,
    items: [
      { title: 'History & Heritage', desc: '175+ years of Protestant heritage in Northern Malabar', icon: HistoryIcon, href: '#history' },
      { title: "Bishop's Message", desc: 'Pastoral vision and guidance from the Diocesan Bishop', icon: BishopIcon, href: '#bishop' },
      { title: 'Administration & Governance', desc: 'Diocesan leadership, office bearers, and synod delegation', icon: GovernanceIcon, href: '#admin' },
      { title: 'Lights of our Diocese', desc: 'Our spiritual leaders, missionaries, and dedicated workers', icon: ConstitutionIcon, href: '#lights' },
    ]
  },
  {
    name: 'Our Churches',
    href: '#churches',
    hasMegaMenu: false,
    isLink: true,
  },
  {
    name: 'Institutions',
    href: '#institutions',
    hasMegaMenu: true,
    columns: 2,
    items: [
      { title: 'Schools', desc: 'Mission schools providing quality education across Malabar', icon: SchoolIcon, href: '#schools' },
      { title: 'Colleges', desc: 'Premier Christian arts, science and professional colleges', icon: CollegeIcon, href: '#colleges' },
      { title: 'Hospitals', desc: 'Charitable mission hospitals and community health clinics', icon: HospitalIcon, href: '#hospitals' },
      { title: 'Retreat Center', desc: 'Spiritual renewal and prayer retreat facilities', icon: RetreatCenterIcon, href: '#retreat' },
      { title: 'Book Shop', desc: 'Christian literature, Bibles and diocesan publications', icon: BookShopIcon, href: '#bookshop' },
      { title: 'Child Care Projects', desc: 'Compassionate care and support for children in need', icon: ChildCareIcon, href: '#childcare' },
      { title: 'Social Board', desc: 'Community development and social welfare initiatives', icon: SocialBoardIcon, href: '#socialboard' },
    ]
  },
  {
    name: 'Departments',
    href: '#departments',
    hasDropdown: false,
    isLink: true,
  },
  { name: 'News', href: '#news', isLink: true },
  {
    name: 'Publications',
    href: '#',
    hasDropdown: true,
    items: [
      { title: 'Publications', desc: 'Diocesan magazines, reports, and pastoral letters', icon: MagazineIcon, href: '#publications' },
      { title: 'Readings', desc: 'Daily scripture readings, almanacs, and devotional materials', icon: LiturgyIcon, href: '#readings' },
    ]
  },
  { name: 'Contact', href: '#contact', isLink: true }
];

// Helper function to check if a navigation section is active
const isSectionActive = (item, activeSection) => {
  if (!activeSection) return item.name === 'Home';
  const cleanSec = activeSection.toLowerCase().replace(/^[/#]/, '').replace(/ /g, '-');
  const itemName = item.name.toLowerCase();
  const itemHref = (item.href || '').replace(/^[/#]/, '').toLowerCase();

  if (itemHref === cleanSec || itemName === cleanSec) return true;

  // About Diocese and its sub-pages
  if (item.name === 'About Diocese') {
    const aboutRoutes = [
      'about', 'about-diocese', 'history', 'bishop', 'council', 'admin',
      'lights', 'pioneers', 'former-bishops', 'former-bishop', 'presbyters', 'deacons', 'evangelists',
      'church-workers', 'deputation', 'presbyters-on-study', 'seminary-students',
      'retired-presbyters', 'missionaries', 'retired-evangelists'
    ];
    return aboutRoutes.includes(cleanSec);
  }

  // Our Churches and its sub-pages
  if (item.name === 'Our Churches') {
    const churchRoutes = ['churches', 'our-churches', 'church'];
    return churchRoutes.includes(cleanSec);
  }

  // Institutions and its sub-pages
  if (item.name === 'Institutions') {
    const instRoutes = [
      'institutions', 'our-institutions', 'schools', 'colleges', 'hospitals',
      'retreat', 'bookshop', 'childcare', 'socialboard', 'aided-higher-secondary',
      'aided-high-schools', 'aided-up-schools', 'aided-lp-schools',
      'unaided-higher-secondary', 'unaided-high-schools', 'unaided-up-schools', 'unaided-lp-schools'
    ];
    return instRoutes.includes(cleanSec);
  }

  // Departments
  if (item.name === 'Departments') {
    return ['departments', 'our-departments'].includes(cleanSec);
  }

  // News and Results
  if (item.name === 'News') {
    return ['news', 'results', 'our-results', 'announcements', 'events'].includes(cleanSec);
  }

  // Publications
  if (item.name === 'Publications') {
    return ['publications', 'readings'].includes(cleanSec);
  }

  // Contact
  if (item.name === 'Contact') {
    return ['contact', 'contact-us', 'get-in-touch'].includes(cleanSec);
  }

  // Home
  if (item.name === 'Home') {
    return cleanSec === 'home' || cleanSec === '';
  }

  return false;
};

export default function Navbar({ activeSection = 'home', onSelectSection = () => { } }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const navRef = useRef(null);
  const dropdownTimeoutRef = useRef(null);

  // Throttled scroll listener with requestAnimationFrame to prevent scroll lag & unnecessary re-renders
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrolled = window.scrollY > 30;
          setIsScrolled(prev => (prev !== scrolled ? scrolled : prev));
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Hover handlers with slight grace delay to prevent flickering
  const handleMouseEnter = (name) => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setActiveDropdown(name);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 120);
  };

  const handleLinkClick = (e, sectionName, href) => {
    if (href === '#') {
      e.preventDefault();
      return;
    }
    if (href && href.startsWith('#')) {
      e.preventDefault();
      onSelectSection(href.substring(1));
      setActiveDropdown(null);
      setMobileMenuOpen(false);
    }
  };

  return (
    <>
      {/* ==========================================
          STICKY FLOATING NAVBAR CONTAINER
      ========================================== */}
      <header className="fixed top-0 left-0 right-0 z-40 pointer-events-none transition-all duration-500">
        <div
          ref={navRef}
          className={`pointer-events-auto mx-4 sm:mx-6 lg:mx-8 xl:mx-auto max-w-[1440px] transition-all duration-500 ease-out ${isScrolled ? 'mt-[10px] sm:mt-[14px]' : 'mt-[16px] sm:mt-[20px]'
            }`}
        >
          <nav
            aria-label="Global Diocese Navigation"
            className={`w-full rounded-full transition-all duration-500 flex items-center justify-between px-5 sm:px-8 xl:px-10 ${isScrolled
              ? 'h-[72px] bg-white/[0.94] backdrop-blur-[26px] border border-[#E8DDD7] shadow-[0_20px_45px_-10px_rgba(123,30,58,0.15),0_8px_20px_rgba(0,0,0,0.06)]'
              : 'h-[86px] bg-white/[0.82] backdrop-blur-[22px] border border-[#E8DDD7] shadow-[0_15px_40px_-10px_rgba(123,30,58,0.08),0_4px_12px_rgba(0,0,0,0.03)]'
              }`}
          >
            {/* ==========================================
                LEFT SIDE: SACRED BRAND BADGE & TYPOGRAPHY
            ========================================== */}
            <a
              href="#home"
              onClick={(e) => handleLinkClick(e, 'home', '#home')}
              className="flex items-center shrink-0 text-decoration-none transition-transform duration-300 hover:scale-[1.02]"
            >
              <img
                src={logo}
                alt="CSI Diocese of Malabar"
                className={`w-auto object-contain transition-all duration-500 ${isScrolled ? 'h-10 sm:h-[44px]' : 'h-12 sm:h-[54px]'
                  }`}
              />
            </a>

            {/* ==========================================
                CENTER NAVIGATION (DESKTOP)
            ========================================== */}
            <div
              className="hidden lg:flex items-center space-x-[24px] xl:space-x-[40px] h-full"
              onMouseLeave={handleMouseLeave}
            >
              {NAVIGATION_ITEMS.map((item) => {
                const isCurrentPage = isSectionActive(item, activeSection);
                const isItemActive = isCurrentPage || activeDropdown === item.name;

                return (
                  <div
                    key={item.name}
                    className="relative h-full flex items-center group"
                    onMouseEnter={() => (item.hasMegaMenu || item.hasDropdown) ? handleMouseEnter(item.name) : setActiveDropdown(null)}
                  >
                    <a
                      href={item.href}
                      onClick={(e) => handleLinkClick(e, item.name, item.href)}
                      className={`relative px-1 py-2 font-sans font-medium text-[14px] xl:text-[15px] transition-all duration-200 flex items-center gap-1 group-hover:-translate-y-[2px] ${isItemActive ? 'text-primary font-semibold' : 'text-[#4B2233] hover:text-primary'
                        }`}
                    >
                      {item.name}
                      {(item.hasMegaMenu || item.hasDropdown) && (
                        <ChevronDown
                          className={`ml-0.5 transition-transform duration-300 ${activeDropdown === item.name ? 'rotate-180 text-accent' : 'text-[#7B6B66] group-hover:text-primary'
                            }`}
                        />
                      )}

                      {/* SPECIAL DESIGN DETAIL: Champagne Gold line with small circular gold indicator */}
                      <span className="absolute bottom-0 left-0 w-full flex justify-center pointer-events-none">
                        {/* Underline base */}
                        <span
                          className={`h-[2px] bg-accent transition-all duration-300 ease-out ${isCurrentPage ? 'w-full opacity-100' : 'w-0 opacity-0 group-hover:w-full group-hover:opacity-100'
                            }`}
                        ></span>
                        {/* Gold Dot Indicator */}
                        <span
                          className={`absolute -bottom-[3px] w-2 h-2 rounded-full bg-accent shadow-glow transition-all duration-300 ${isCurrentPage ? 'scale-100 opacity-100' : 'scale-0 opacity-0 group-hover:scale-100 group-hover:opacity-100'
                            }`}
                        ></span>
                      </span>
                    </a>

                    {/* ==========================================
                        FLOATING MEGA MENU / DROPDOWN CONTAINER
                    ========================================== */}
                    {(item.hasMegaMenu || item.hasDropdown) && activeDropdown === item.name && (
                      <div
                        className={`absolute top-[calc(100%-8px)] z-50 transition-all duration-300 ease-out animate-slide-down max-w-[calc(100vw-32px)] ${
                          item.name === 'About Diocese'
                            ? 'left-0 lg:left-[-30px] xl:left-[-60px] translate-x-0'
                            : item.name === 'Institutions'
                            ? 'right-[-80px] lg:right-[-140px] xl:right-[-180px] left-auto translate-x-0'
                            : item.name === 'Publications'
                            ? 'right-0 lg:right-[-20px] left-auto translate-x-0'
                            : 'left-1/2 -translate-x-1/2'
                        } ${item.hasMegaMenu ? 'w-[740px] xl:w-[820px]' : 'w-[360px]'
                          }`}
                        onMouseEnter={() => handleMouseEnter(item.name)}
                      >
                        {/* Invisible bridge for mouse hover */}
                        <div className="h-[12px] w-full" />

                        {/* Mega Menu White Luxury Card */}
                        <div className="bg-white/95 backdrop-blur-[28px] rounded-[28px] border border-[#E8DDD7] shadow-[0_25px_60px_-15px_rgba(123,30,58,0.18),0_0_1px_1px_rgba(232,221,215,0.7)] p-6 xl:p-7 overflow-hidden relative">
                          {/* Decorative Champagne Gold Top Bar */}
                          <div className="absolute top-0 left-8 right-8 h-[2px] bg-gradient-to-r from-transparent via-[#D4A64A] to-transparent opacity-75"></div>

                          {/* Header / Context Badge */}
                          <div className="flex items-center justify-between pb-4 mb-4 border-b border-[#F5EFEE]">
                            <span className="font-serif italic font-semibold text-xs uppercase tracking-widest text-[#7B1E3A] px-3 py-1 bg-[#FAF6F2] rounded-full border border-[#E8DDD7]/60">
                              Explore {item.name}
                            </span>
                            <span className="text-[11px] font-medium text-[#8B8683] flex items-center gap-1">
                              Church of South India <span className="w-1 h-1 rounded-full bg-accent"></span> Malabar
                            </span>
                          </div>

                          {/* Menu Items Grid */}
                          <div
                            className={`grid gap-3 ${item.hasMegaMenu ? 'grid-cols-2' : 'grid-cols-1'
                              }`}
                          >
                            {item.items.map((subItem, idx) => {
                              const IconComponent = subItem.icon;
                              return (
                                <a
                                  key={idx}
                                  href={subItem.href}
                                  onClick={(e) => handleLinkClick(e, item.name, subItem.href)}
                                  className="group/item flex items-start gap-4 p-3.5 rounded-[20px] bg-white hover:bg-[#FAF6F2] border border-transparent hover:border-[#E8DDD7]/70 transition-all duration-200 text-left"
                                >
                                  {/* Icon container beside menu item */}
                                  <div className="shrink-0 w-11 h-11 rounded-[16px] bg-[#F5F1EE] border border-[#E8DDD7]/60 flex items-center justify-center text-primary group-hover/item:bg-primary group-hover/item:text-white group-hover/item:shadow-[0_4px_15px_rgba(123,30,58,0.25)] transition-all duration-300">
                                    <IconComponent className="w-5 h-5" />
                                  </div>

                                  {/* Text & Description */}
                                  <div className="flex flex-col">
                                    <span className="font-sans font-bold text-[#4B2233] text-[15px] group-hover/item:text-primary transition-colors duration-200 flex items-center justify-between">
                                      {subItem.title}
                                      <span className="text-xs text-[#D4A64A] opacity-0 -translate-x-2 group-hover/item:opacity-100 group-hover/item:translate-x-0 transition-all duration-200">
                                        →
                                      </span>
                                    </span>
                                    <span className="text-[#7B6B66] text-[12px] leading-relaxed mt-0.5 font-sans">
                                      {subItem.desc}
                                    </span>
                                  </div>
                                </a>
                              );
                            })}
                          </div>

                          {/* Footer Quick Action in Mega Menu */}
                          {item.hasMegaMenu && (
                            <div className="mt-5 pt-4 border-t border-[#F5EFEE] flex items-center justify-between px-2 text-xs">
                              <span className="text-[#5F5A58] italic font-serif">
                                "Called to serve, unite, and witness in faith across Northern Kerala."
                              </span>
                              <a
                                href="#contact"
                                className="font-sans font-bold text-primary hover:text-primary-dark underline underline-offset-4 flex items-center gap-1 transition-colors"
                              >
                                Request Pastoral Help →
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ==========================================
                RIGHT SIDE: UTILITY ICONS & CTA BUTTON
            ========================================== */}
            <div className="flex items-center gap-3 xl:gap-4 shrink-0">
              {/* Search Icon with slight hover rotation */}
              <button
                type="button"
                onClick={() => setSearchModalOpen(true)}
                aria-label="Search Diocese Archives & Parishes"
                className="w-10 h-10 rounded-full bg-[#F5F1EE] hover:bg-[#FAF6F2] border border-[#E8DDD7]/70 text-[#4B2233] hover:text-primary flex items-center justify-center transition-all duration-300 group shadow-sm hover:shadow"
              >
                <SearchIcon className="w-4 h-4 xl:w-5 xl:h-5 text-[#4B2233] group-hover:text-primary transition-transform duration-300 group-hover:rotate-12 group-hover:scale-110" />
              </button>


              {/* Mobile Menu Trigger Icon */}
              <button
                type="button"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open navigation drawer"
                className="lg:hidden w-11 h-11 rounded-full bg-primary text-white flex items-center justify-center shadow-button active:scale-95 transition-all"
              >
                <MenuIcon className="w-6 h-6" />
              </button>
            </div>
          </nav>
        </div>
      </header>

      {/* ==========================================
          MOBILE PREMIUM SLIDE-OUT MENU DRAWER
      ========================================== */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden flex justify-end">
          {/* Blurred background overlay */}
          <div
            className="fixed inset-0 bg-[#4B2233]/45 backdrop-blur-[6px] animate-fade-in"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />

          {/* Rounded Drawer Container */}
          <div className="relative z-10 w-[90%] max-w-[420px] h-full bg-[#FAF8F6] text-[#4B2233] rounded-l-[32px] shadow-[0_0_60px_rgba(0,0,0,0.3)] flex flex-col justify-between overflow-hidden animate-slide-down border-l border-[#E8DDD7]">

            {/* Drawer Header */}
            <div className="p-6 bg-white/90 border-b border-[#E8DDD7] flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary overflow-hidden flex items-center justify-center text-white shadow-badge-glow">
                  <img src={logo} alt="CSI Diocese of Malabar Logo" className="w-[80%] h-[80%] object-contain" />
                </div>
                <div>
                  <span className="font-serif font-bold text-[#4B2233] text-lg block leading-tight">Diocese of Malabar</span>
                  <span className="font-sans text-[9px] font-extrabold tracking-widest text-[#7B6B66] uppercase block">Syro-Malankara Catholic</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="w-10 h-10 rounded-full bg-[#F5F1EE] text-[#4B2233] hover:bg-primary hover:text-white flex items-center justify-center transition-colors"
                aria-label="Close menu"
              >
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Accordion Navigation Items */}
            <div className="flex-1 overflow-y-auto p-6 space-y-3 divide-y divide-[#E8DDD7]/50">
              {/* Search & Language Bar in Mobile */}
              <div className="flex items-center gap-3 pb-3">
                <button
                  onClick={() => { setMobileMenuOpen(false); setSearchModalOpen(true); }}
                  className="flex-1 py-3 px-4 rounded-2xl bg-white border border-[#E8DDD7] text-[#7B6B66] flex items-center justify-between text-sm font-medium shadow-2xs"
                >
                  <span className="flex items-center gap-2 text-[#4B2233]">
                    <SearchIcon className="w-4 h-4 text-primary" /> Search diocese...
                  </span>
                  <span className="text-xs font-bold text-accent px-2 py-0.5 bg-[#FAF6F2] rounded-full">Explore</span>
                </button>


              </div>

              {NAVIGATION_ITEMS.map((item) => {
                const isAccordionOpen = openAccordion === item.name;
                const isCurrent = isSectionActive(item, activeSection);

                if (item.isLink) {
                  return (
                    <div key={item.name} className="pt-3">
                      <a
                        href={item.href}
                        onClick={(e) => { handleLinkClick(e, item.name, item.href); }}
                        className={`py-3 px-4 rounded-2xl font-sans font-bold text-[17px] flex items-center justify-between transition-colors ${isCurrent
                          ? 'bg-primary text-white shadow-button'
                          : 'bg-white text-[#4B2233] hover:bg-[#F5F1EE]'
                          }`}
                      >
                        <span>{item.name}</span>
                        {isCurrent && <span className="w-2 h-2 rounded-full bg-accent animate-pulse"></span>}
                      </a>
                    </div>
                  );
                }

                // Accordion item with Subitems
                return (
                  <div key={item.name} className="pt-3">
                    <button
                      type="button"
                      onClick={() => setOpenAccordion(isAccordionOpen ? null : item.name)}
                      className={`w-full py-3 px-4 rounded-2xl font-sans font-bold text-[17px] flex items-center justify-between transition-all duration-200 ${isAccordionOpen
                        ? 'bg-[#7B1E3A]/10 text-[#7B1E3A] border border-primary/20'
                        : 'bg-white text-[#4B2233] hover:bg-[#F5F1EE]'
                        }`}
                    >
                      <span className="flex items-center gap-2">
                        {item.name}
                        <span className="text-xs font-normal text-accent px-2 py-0.5 bg-[#FAF6F2] rounded-full border border-border/60">
                          {item.items.length} items
                        </span>
                      </span>
                      <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isAccordionOpen ? 'rotate-180 text-primary' : 'text-[#7B6B66]'}`} />
                    </button>

                    {/* Collapsible content */}
                    {isAccordionOpen && (
                      <div className="mt-2 pl-2 space-y-1.5 animate-fade-in">
                        {item.items.map((sub, idx) => {
                          const SubIcon = sub.icon;
                          return (
                            <a
                              key={idx}
                              href={sub.href}
                              onClick={(e) => handleLinkClick(e, item.name, sub.href)}
                              className="flex items-center gap-3.5 p-3 rounded-2xl bg-white hover:bg-[#FAF6F2] border border-[#E8DDD7]/60 text-sm font-semibold text-[#4B2233] transition-all"
                            >
                              <div className="w-9 h-9 rounded-xl bg-[#FAF6F2] flex items-center justify-center text-primary border border-border/50 shrink-0">
                                <SubIcon className="w-4 h-4" />
                              </div>
                              <div className="flex flex-col">
                                <span className="leading-snug">{sub.title}</span>
                                <span className="text-[11px] font-normal text-[#7B6B66] line-clamp-1">{sub.desc}</span>
                              </div>
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Clergy Login Link */}
              {/* <div className="pt-3">
                <a
                  href="#login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-full py-3.5 px-4 rounded-2xl bg-white border border-[#E8DDD7] font-semibold text-[15px] text-[#4B2233] flex items-center justify-center gap-2 hover:bg-[#FAF6F2]"
                >
                  <LoginIcon className="w-5 h-5 text-primary" />
                  <span>Clergy & Parish Member Portal Login</span>
                </a>
              </div>
            </div> */}

              {/* Drawer Footer CTA (Donate Button Fixed at Bottom) */}
              {/* <div className="p-6 bg-white border-t border-[#E8DDD7] shrink-0">
              <a
                href="#donate"
                onClick={(e) => handleLinkClick(e, 'donate', '#donate')}
                className="w-full bg-[#7B1E3A] hover:bg-[#5E132A] text-white font-sans font-bold text-base rounded-full py-4 px-6 shadow-button hover:shadow-[0_15px_35px_rgba(123,30,58,0.45)] flex items-center justify-center gap-3 transition-all duration-300"
              >
                <span className="w-2.5 h-2.5 rounded-full bg-accent animate-pulse shadow-glow"></span>
                <span>Donate to Diocesan Mission</span>
              </a>
              <p className="text-[11px] text-center text-[#7B6B66] mt-2.5 font-serif italic">
                "God loves a cheerful giver." — 2 Corinthians 9:7
              </p> */}
            </div>
          </div>
        </div>
      )}

      {/* ==========================================
          INTERACTIVE SEARCH MODAL (SPECIAL EXPERIENCE)
      ========================================== */}
      {searchModalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-24 px-4">
          <div
            className="fixed inset-0 bg-[#4B2233]/60 backdrop-blur-md animate-fade-in"
            onClick={() => setSearchModalOpen(false)}
            aria-hidden="true"
          />
          <div className="relative z-10 w-full max-w-2xl bg-white rounded-3xl border border-[#E8DDD7] shadow-[0_30px_80px_rgba(0,0,0,0.35)] overflow-hidden p-6 animate-slide-down">
            <div className="flex items-center gap-3 pb-4 border-b border-[#E8DDD7]">
              <SearchIcon className="w-6 h-6 text-primary shrink-0 animate-pulse" />
              <input
                type="text"
                autoFocus
                placeholder="Search churches, bishops, institutions, sermons, or clergy..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full text-lg font-sans font-semibold text-[#4B2233] placeholder-[#8B8683] bg-transparent border-none outline-none"
              >
              </input>
              <button
                type="button"
                onClick={() => setSearchModalOpen(false)}
                className="p-2 rounded-full hover:bg-[#FAF6F2] text-[#7B6B66]"
                title="Press ESC to close"
              >
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="py-5">
              <span className="text-xs font-bold uppercase tracking-widest text-[#7B1E3A] block mb-3 font-serif">
                Quick Heritage & Diocesan Archives
              </span>
              <div className="grid grid-cols-2 gap-2.5">
                {[
                  { title: 'St. Thomas Cathedral, Calicut', category: 'Churches' },
                  { title: 'Basel Evangelical Mission Legacy (1839)', category: 'Heritage' },
                  { title: 'Malabar Christian College, Kozhikode', category: 'Institution' },
                  { title: 'Order of Service for Holy Communion', category: 'Liturgy' },
                  { title: 'Diocesan Bishop Pastoral Address 2026', category: 'Publication' },
                  { title: 'CSI Malabar Mission Hospital, Chombal', category: 'Healthcare' },
                ].map((s, i) => (
                  <button
                    key={i}
                    onClick={() => { setSearchModalOpen(false); onSelectSection('churches'); }}
                    className="p-3.5 rounded-2xl bg-[#FAF8F6] hover:bg-primary hover:text-white text-left transition-all duration-200 group border border-[#EEE7E2] flex items-center justify-between"
                  >
                    <div>
                      <span className="text-[13px] font-bold block group-hover:text-white text-[#4B2233]">{s.title}</span>
                      <span className="text-[11px] font-medium text-[#8B8683] group-hover:text-[#E6C67A]">{s.category}</span>
                    </div>
                    <span className="text-accent group-hover:translate-x-1 transition-transform">→</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="bg-[#FAF6F2] -mx-6 -mb-6 p-4 border-t border-[#EEE7E2] flex items-center justify-between text-xs text-[#7B6B66]">
              <span>Tip: Type any parish name, school, or pastor name</span>
              <span className="px-2 py-0.5 rounded bg-white border border-border font-mono font-bold">ESC to close</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
